import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabaseClient';

const CustomerOrdersModal = ({ customer, onClose }) => {
  if (!customer) return null;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        let query = supabase
          .from('orders')
          .select('id, order_number, order_date, total_amount, status, customer_id, phone, customer_name, order_items ( brand, model, service_type, memo )')
          .order('order_date', { ascending: false });

        if (customer?.customerId) {
          query = query.eq('customer_id', customer.customerId);
        } else if (customer?.phone) {
          query = query.eq('phone', customer.phone);
        } else if (customer?.name) {
          query = query.eq('customer_name', customer.name);
        }

        const { data, error } = await query;

        if (error) throw error;
        if (!mounted) return;

        setOrders(data || []);
      } catch (e) {
        if (!mounted) return;
        setOrders([]);
        setErrorMessage(e?.message || '주문 내역을 불러오지 못했습니다.');
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [customer?.customerId, customer?.phone, customer?.name]);

  const totalAmountSum = useMemo(() => {
    return (orders || []).reduce((sum, o) => sum + Number(o?.total_amount || 0), 0);
  }, [orders]);

  const avgOrderAmount = useMemo(() => {
    if (!orders?.length) return 0;
    return Math.round(totalAmountSum / orders.length);
  }, [orders, totalAmountSum]);

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '완료':
        return 'bg-success/10 text-success';
      case '진행중':
        return 'bg-accent/10 text-accent';
      case '취소':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-card shadow-card">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">주문 내역</h2>
            <p className="text-sm text-muted-foreground">
              {customer?.name} · {customer?.phone}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="닫기"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6">
          {errorMessage && (
            <div className="mb-6 rounded-lg bg-error/10 p-4">
              <p className="text-sm text-error">{errorMessage}</p>
            </div>
          )}

          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-primary/10 p-4">
              <p className="text-sm text-muted-foreground">총 주문 건수</p>
              <p className="mt-1 text-2xl font-bold text-primary">
                {isLoading ? '-' : `${orders?.length}건`}
              </p>
            </div>
            <div className="rounded-lg bg-accent/10 p-4">
              <p className="text-sm text-muted-foreground">총 결제 금액</p>
              <p className="mt-1 text-2xl font-bold text-accent">
                {isLoading ? '-' : `${totalAmountSum?.toLocaleString('ko-KR')}원`}
              </p>
            </div>
            <div className="rounded-lg bg-success/10 p-4">
              <p className="text-sm text-muted-foreground">평균 주문 금액</p>
              <p className="mt-1 text-2xl font-bold text-success">
                {isLoading ? '-' : `${avgOrderAmount?.toLocaleString('ko-KR')}원`}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {orders?.map((order) => (
              <div key={order?.id} className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-foreground">
                        {order?.order_number || `#${order?.id}`}
                      </h3>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(order?.status)}`}>
                        {order?.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      주문일: {formatDate(order?.order_date)}
                    </p>
                  </div>
                  <p className="text-lg font-bold text-foreground">
                    {Number(order?.total_amount || 0)?.toLocaleString('ko-KR')}원
                  </p>
                </div>

                <div className="mb-3 space-y-2 border-t border-border pt-3">
                  {order?.order_items?.map((item, index) => (
                    <div key={index} className="rounded-md bg-muted/30 px-3 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <Icon name="Package" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">
                            {[item?.brand, item?.model].filter(Boolean).join(' ') || '-'}
                          </span>
                        </div>
                        <span className="text-muted-foreground">{item?.service_type}</span>
                      </div>
                      {item?.memo && (
                        <p className="mt-1 text-xs text-muted-foreground whitespace-pre-line">
                          메모: {item?.memo}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
                  <div className="text-muted-foreground" />
                  <div />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrdersModal;